import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslate } from '../hooks/useTranslate'
import { DataSource, FileColumn, Folder } from '../types'
import DataObjectConfig from './modal/DataObjectConfig'
import ColumnConfigurationTable from './modal/ColumnConfigurationTable'
import SchemaComparisonModal from './SchemaComparisonModal'
import { useSchemaComparison } from '../hooks/useSchemaComparison'
import { dataObjectService } from '../services/dataObjectService'
import { reportService } from '../services/reportService'
import { dqRuleService } from '../services/dqRuleService'
import { useToast } from '../hooks/useToast'
import { useAsyncJob } from '../hooks/useAsyncJob'
import { useLanguage } from '../contexts/LanguageContext'
import { asyncDataDiagnosisService, JobStatus } from '../services/asyncDataDiagnosisService'
import Loading from './common/Loading'
import { getButtonClasses, getButtonStyles, normalizeDataType } from '../constants'
import { FileIcon } from './common/FileIcon'
import { useNavigate } from 'react-router-dom'
import { BiSolidReport } from 'react-icons/bi'
import { RiInformationFill } from 'react-icons/ri'
import { IoMdCloudUpload } from 'react-icons/io'
import { FaEdit, FaAngleDoubleRight } from 'react-icons/fa'
import { GrCycle } from 'react-icons/gr'
import { VscGithubAction } from "react-icons/vsc"
import ContextualHelpLink from './training/ContextualHelpLink'
// DEPRECATED: columnService will be removed in favor of data_description field

interface DataObjectActionsModalProps {
    isOpen: boolean
    dataObject?: DataSource
    onClose: () => void
    onViewReport: (dataObject: DataSource) => void
    onUpdateMetadata: (id: number, updates: { name?: string; description?: string; columns?: FileColumn[]; dataObjectUuid?: string; targetLanguage?: string }) => void
    onUploadFile: (dataObject: DataSource) => void
    folders: Folder[]
    onMoveRequest: (dataObject: DataSource) => void
    runningJobs?: JobStatus[]
    getJobForDataObject?: (dataObjectUuid: string) => JobStatus | undefined
    onDataSourceUpdated?: (dataSource: DataSource) => void // Optional callback for notifying about data source changes
    onFastRefresh?: () => void // Optional callback for fast data refresh instead of page reload
    // New: Progress persistence functions
    addWatchedObject?: (dataObjectUuid: string) => void
    removeWatchedObject?: (dataObjectUuid: string) => void
    getPersistedProgressForObject?: (dataObjectUuid: string) => any
}

// Lightweight modal presenting three actions for a data object.
// Accessibility contract: role="dialog", aria-modal, labelled by title id, Escape closes.
const DataObjectActionsModal: React.FC<DataObjectActionsModalProps> = ({
    isOpen,
    dataObject,
    onClose,
    onViewReport,
    onUpdateMetadata,
    onUploadFile,
    folders,
    onMoveRequest,
    runningJobs = [],
    getJobForDataObject,
    onDataSourceUpdated,
    onFastRefresh,
    addWatchedObject,
    removeWatchedObject,
    getPersistedProgressForObject
}) => {
    const dialogRef = useRef<HTMLDivElement | null>(null)
    const { t } = useTranslate()
    const firstButtonRef = useRef<HTMLButtonElement | null>(null)
    const nameInputRef = useRef<HTMLInputElement | null>(null)
    const descriptionRef = useRef<HTMLTextAreaElement | null>(null)
    const [editing, setEditing] = useState(false)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [nameError, setNameError] = useState('')
    const [columns, setColumns] = useState<FileColumn[]>([])
    const [columnsLoading, setColumnsLoading] = useState(false)
    const [columnsError, setColumnsError] = useState('')
    const [columnSearch, setColumnSearch] = useState('')
    const [columnRules, setColumnRules] = useState<Record<string, number>>({}) // Track rule counts per column
    const [selectedColumnsForReport, setSelectedColumnsForReport] = useState<string[]>([])
    // Add state for existing rules - only used for Databricks delta tables, not file uploads
    const [existingRules, setExistingRules] = useState<any[]>([]);
    const [rulesLoading, setRulesLoading] = useState(false);
    // Add state for all available rules for reuse functionality
    const [allAvailableRules, setAllAvailableRules] = useState<any[]>([]);
    const [allRulesLoading, setAllRulesLoading] = useState(false);
    const [saving, setSaving] = useState(false)
    const [retriggerAnalysis, setRetriggerAnalysis] = useState(false)
    const [loadingRetrigger, setLoadingRetrigger] = useState(false) // Loading state for retrigger button
    const [loadingReplace, setLoadingReplace] = useState(false) // Loading state for replace/upload button
    const [jobWasFound, setJobWasFound] = useState(false) // Track if we found an existing job
    const [autoTriggeredStatus, setAutoTriggeredStatus] = useState(false) // Track if we auto-triggered status API
    const [retriggerWasClicked, setRetriggerWasClicked] = useState(false) // Track if retrigger was clicked to refresh page on close
    const [currentDataObjectName, setCurrentDataObjectName] = useState<string | null>(null)
    const [currentDataObjectUuid, setCurrentDataObjectUuid] = useState<string | null>(null)
    const [currentJobId, setCurrentJobId] = useState<string | null>(null) // Track current job ID
    const [refreshInterval, setRefreshInterval] = useState<number | null>(null) // Auto-refresh for status sync
    const { push: pushToast } = useToast()
    const navigate = useNavigate()
    const { activeLanguage } = useLanguage()
    const [translatingMetadata, setTranslatingMetadata] = useState(false)
    // Keep original (English) description/columns so we can revert on language change
    const originalDescriptionRef = useRef<string>('')
    const originalColumnsRef = useRef<FileColumn[]>([])
    // Temporary feature flag to hide scoped-report selection UI
    const ENABLE_SCOPED_REPORT_UI = false

    // --- Schema comparison state for retrigger ---
    const schemaComparison = useSchemaComparison()
    const [showSchemaModal, setShowSchemaModal] = useState(false)

    // Reset ALL modal state when dataObject changes (fixes state bleeding between different data objects)
    useEffect(() => {
        if (!dataObject) return;

        // Modal opened for new data object, resetting state

        // CRITICAL: Clean up previous data object from watched objects to prevent cross-contamination
        if (currentDataObjectUuid && removeWatchedObject && currentDataObjectUuid !== (dataObject.uuid || dataObject.id.toString())) {

            removeWatchedObject(currentDataObjectUuid);
        }

        // Reset all analysis/progress state
        setRetriggerAnalysis(false);
        setLoadingRetrigger(false);
        setLoadingReplace(false);
        setJobWasFound(false);
        setAutoTriggeredStatus(false);
        setCurrentDataObjectName(null);
        setCurrentDataObjectUuid(null);
        setCurrentJobId(null);
        schemaComparison.reset();
        setShowSchemaModal(false);

        // CRITICAL: For processing data objects, NEVER set retrigger state - always look for real jobs
        if (dataObject.status === 'processing' || dataObject.status === 'analyzing') {

            setRetriggerAnalysis(false); // Ensure this stays false for processing objects
        }

        // CRITICAL: Stop and reset asyncJob state to prevent bleeding between data objects
        if (asyncJob?.stopPolling) {

            asyncJob.stopPolling();
        }
        if (asyncJob?.reset) {

            asyncJob.reset();
        }

        // Reset editing state
        setEditing(false);
        setNameError('');
        setName(dataObject.name || '');
        setDescription(dataObject.description || '');

        // Reset column state
        setColumns([]);
        setColumnsLoading(false);
        setColumnsError('');
        setColumnSearch('');
        setColumnRules({});
        setSelectedColumnsForReport([]);

        // Reset rules state
        setExistingRules([]);
        setRulesLoading(false);
        setSaving(false);

    }, [dataObject?.id]); // Only depend on dataObject.id to avoid unnecessary resets

    // Async job management for retrigger analysis
    const asyncJob = useAsyncJob({
        onJobUpdate: (status) => {
            // When progress reaches 100%, immediately update status to processed and enable report
            if (status.progress >= 100 && onDataSourceUpdated && dataObject && dataObject.status !== 'processed') {

                onDataSourceUpdated({ ...dataObject, status: 'processed' });

                // IMPORTANT: Reset job tracking state immediately when progress reaches 100%
                // This enables the View Report button right away
                setRetriggerAnalysis(false);
                setJobWasFound(false);
                setCurrentJobId(null);

                // Also trigger fast refresh to update the UI immediately
                if (onFastRefresh) {

                    setTimeout(() => onFastRefresh(), 500); // Small delay to allow backend to update
                }
            }
        },
        onJobComplete: async (status) => {
            if (status.status === 'SUCCESS') {


                // Reset all job tracking state - analysis is complete
                setRetriggerAnalysis(false)
                setJobWasFound(false)
                setAutoTriggeredStatus(false)
                setCurrentJobId(null) // Clear job ID since analysis is complete
                setCurrentDataObjectName(null)
                setCurrentDataObjectUuid(null)



                // Stop watching this object since job is complete
                if (removeWatchedObject && dataObject?.uuid) {
                    removeWatchedObject(dataObject.uuid);

                }

                // Update data source status immediately via callback
                if (onDataSourceUpdated && dataObject) {
                    onDataSourceUpdated({ ...dataObject, status: 'processed' })

                }

                // Auto-refresh data when report is generated successfully
                setTimeout(() => {

                    if (onFastRefresh) {
                        onFastRefresh();
                    } else {
                        window.location.reload();
                    }
                }, 1000);
            } else if (status.status === 'FAILED') {


                // Reset job tracking state on failure
                setRetriggerAnalysis(false)
                setJobWasFound(false)
                setAutoTriggeredStatus(false)
                setCurrentJobId(null)
                setCurrentDataObjectName(null)
                setCurrentDataObjectUuid(null)

                // Stop watching this object since job failed
                if (removeWatchedObject && dataObject?.uuid) {
                    removeWatchedObject(dataObject.uuid);

                }

                // Keep modal open on failure so user can try again
            }
        }
    })

    // Calculate if this specific data object has an active job (used throughout component)
    // PERFORMANCE: Use useMemo to prevent recalculation on every render
    const hasActiveJob = useMemo(() => {
        // Job is active if it exists, is polling, and not complete
        const jobExists = asyncJob.jobStatus && !asyncJob.isComplete;
        // Also check if progress is less than 100% to ensure button enables at 100%
        const notComplete = asyncJob.progress < 100;
        return jobExists && notComplete;
    }, [asyncJob.jobStatus, asyncJob.isComplete, asyncJob.progress])
    const isCurrentlyPolling = asyncJob.isPolling

    // FIXED: Disable buttons when there's an active async job OR when starting analysis
    // This prevents the mismatch where both buttons are active during analysis
    // BUT allows View Report to be enabled when progress reaches 100%
    const hasJobForThisDataObject = useMemo(() => {
        // Don't disable if job is complete (progress = 100%)
        if (asyncJob.progress >= 100) {
            return false; // Enable View Report when progress reaches 100%
        }
        return hasActiveJob || isCurrentlyPolling || retriggerAnalysis;
    }, [hasActiveJob, isCurrentlyPolling, retriggerAnalysis, asyncJob.progress])

    // For progress display, we want to show progress for any job (active or starting)
    const hasJobForProgressDisplay = useMemo(() => Boolean(jobWasFound || hasActiveJob || currentJobId || isCurrentlyPolling || retriggerAnalysis), [jobWasFound, hasActiveJob, currentJobId, isCurrentlyPolling, retriggerAnalysis])

    // Restore persisted progress when modal opens - but verify job is still active
    useEffect(() => {
        if (!isOpen || !dataObject || !getPersistedProgressForObject) return;

        const persistedProgress = getPersistedProgressForObject(dataObject.uuid || dataObject.id.toString());

        if (persistedProgress && ['RUNNING', 'PENDING'].includes(persistedProgress.status)) {
            // Check if this is recent (within last 15 minutes) to avoid restoring stale jobs
            const fifteenMinutesAgo = Date.now() - (15 * 60 * 1000);
            if (persistedProgress.timestamp > fifteenMinutesAgo) {

                // Verify job is still active before restoring state
                asyncDataDiagnosisService.getJobStatus(persistedProgress.jobId)
                    .then(currentStatus => {

                        if (['RUNNING', 'PENDING'].includes(currentStatus.status)) {
                            // Job is still active - restore state
                            setCurrentDataObjectName(dataObject.name);
                            setCurrentDataObjectUuid(dataObject.uuid || dataObject.id.toString());
                            setCurrentJobId(persistedProgress.jobId);
                            setJobWasFound(true);
                            // FIXED: Only set retriggerAnalysis if we're actually starting a new job
                            // Don't set it to true for ongoing jobs - that creates wrong UI state
                            setRetriggerAnalysis(false);



                            // Resume polling for this job
                            if (addWatchedObject) {
                                addWatchedObject(dataObject.uuid || dataObject.id.toString());
                            }

                            // Start async job polling to get real-time updates
                            asyncJob.startPolling(persistedProgress.jobId);
                        } else {

                            // Job is complete - clear persisted progress
                            if (removeWatchedObject) {
                                removeWatchedObject(dataObject.uuid || dataObject.id.toString());
                            }
                        }
                    })
                    .catch(error => {
                        console.debug('🔄 [PERSISTENCE] Failed to verify job status, clearing persisted progress:', error);
                        if (removeWatchedObject) {
                            removeWatchedObject(dataObject.uuid || dataObject.id.toString());
                        }
                    });
            } else {

                // Stale progress - clear it
                if (removeWatchedObject) {
                    removeWatchedObject(dataObject.uuid || dataObject.id.toString());
                }
            }
        } else {

        }
    }, [isOpen, dataObject, getPersistedProgressForObject, addWatchedObject, removeWatchedObject, asyncJob]);

    // Helper function to detect if DataSource is a Databricks table
    const isDatabricksTable = (dataSource: DataSource): boolean => {
        const originalFilename = dataSource.originalFileName || dataSource.name
        const sourceType = dataSource.data_description?.source_type || ''
        return sourceType === 'delta_table' || originalFilename.toLowerCase().endsWith('.delta')
    }

    // Extract table name from Databricks DataSource
    const getDatabricksTableName = (dataSource: DataSource): string => {
        const originalFilename = dataSource.originalFileName || dataSource.name
        // Remove .delta extension if present
        return originalFilename.endsWith('.delta')
            ? originalFilename.slice(0, -6)
            : originalFilename
    }

    // Extract table information for display (catalog.schema.table format)
    const getTableInformation = (dataSource: DataSource) => {
        const tableFullName = getDatabricksTableName(dataSource)
        const parts = tableFullName.split('.')

        if (parts.length >= 3) {
            return {
                catalog: parts[0],
                schema: parts[1],
                table: parts.slice(2).join('.'), // In case table name has dots
                fullPath: tableFullName
            }
        } else {
            // Fallback if not in expected format
            return {
                catalog: 'Unknown',
                schema: 'Unknown',
                table: tableFullName,
                fullPath: tableFullName
            }
        }
    }

    // Seamless refresh function to sync job status without user awareness
    const refreshJobStatus = useCallback(async () => {
        if (!dataObject || !isOpen) return;

        try {
            // Silently check for running jobs and update status
            let matchingJob: JobStatus | undefined;

            if (getJobForDataObject) {
                matchingJob = getJobForDataObject(dataObject.uuid || dataObject.id.toString());
            } else if (runningJobs.length > 0) {
                matchingJob = runningJobs.find(job => {
                    // Primary match: data_object_id should match dataObject.uuid
                    const dataObjectIdMatch = job.data_object_id === dataObject.uuid;

                    // Fallback matches for backward compatibility
                    const sourceMatch = job.source_identifier === (dataObject as any).uuid ||
                        job.source_identifier === dataObject.name ||
                        job.source_identifier.includes(dataObject.name)
                    const nameMatch = job.dataset_name === dataObject.name

                    return dataObjectIdMatch || sourceMatch || nameMatch
                })
            }

            if (matchingJob && matchingJob.id !== currentJobId) {
                // Found different job - seamlessly switch to it
                setJobWasFound(true)
                setRetriggerAnalysis(false)
                setCurrentDataObjectName(dataObject.name)
                setCurrentDataObjectUuid((dataObject as any).uuid)
                setCurrentJobId(matchingJob.id)

                // Add to watched objects
                if (addWatchedObject) {
                    addWatchedObject(dataObject.uuid || dataObject.id.toString());
                }

                asyncJob.startPolling(matchingJob.id)
            } else if (!matchingJob && currentJobId) {
                // No matching job found but we have a current job - check if it completed
                try {
                    const jobStatus = await asyncDataDiagnosisService.getJobStatus(currentJobId);
                    if (['SUCCESS', 'FAILED', 'CANCELLED'].includes(jobStatus.status)) {
                        // Job completed - clear tracking state
                        setJobWasFound(false);
                        setCurrentJobId(null);
                        setCurrentDataObjectName(null);
                        setCurrentDataObjectUuid(null);
                        setRetriggerAnalysis(false);

                        // Stop watching this object
                        if (removeWatchedObject) {
                            removeWatchedObject(dataObject.uuid || dataObject.id.toString());
                        }

                        // Update data object status based on job completion
                        if (jobStatus.status === 'SUCCESS' && onDataSourceUpdated) {
                            onDataSourceUpdated({ ...dataObject, status: 'processed' });
                        } else if (jobStatus.status === 'FAILED' && onDataSourceUpdated) {
                            onDataSourceUpdated({ ...dataObject, status: 'error' });
                        }
                    }
                } catch (statusError) {
                    // Job status check failed - likely job was cleaned up
                    console.debug('Job status check failed - assuming job completed:', statusError);
                    setJobWasFound(false);
                    setCurrentJobId(null);
                    setCurrentDataObjectName(null);
                    setCurrentDataObjectUuid(null);
                    setRetriggerAnalysis(false);

                    if (removeWatchedObject) {
                        removeWatchedObject(dataObject.uuid || dataObject.id.toString());
                    }
                }
            }
        } catch (error) {
            // Silent failure - don't disrupt user experience
            console.debug('Background refresh failed:', error)
        }
    }, [dataObject, isOpen, getJobForDataObject, runningJobs, currentJobId, asyncJob, addWatchedObject, removeWatchedObject, onDataSourceUpdated])

    // DIRECT API APPROACH: When modal opens for processing data object, immediately fetch real job status
    // OPTIMIZED: Only run once per modal open and don't conflict with other job discovery
    useEffect(() => {
        if (!isOpen || !dataObject || asyncJob.isPolling || currentJobId) return; // Skip if already has job

        // For processing/analyzing data objects, immediately get current job status
        if (dataObject.status === 'processing' || dataObject.status === 'analyzing') {
            const fetchCurrentJobStatus = async () => {
                try {
                    // First, get all running jobs to find the one for this data object
                    const runningJobsResponse = await asyncDataDiagnosisService.getRunningJobs();

                    // Find the job that matches this data object
                    const matchingJob = runningJobsResponse.find(job => {
                        const dataObjectIdMatch = job.data_object_id === dataObject.uuid;
                        const sourceMatch = job.source_identifier === dataObject.uuid ||
                            job.source_identifier === dataObject.name ||
                            job.source_identifier?.includes(dataObject.name);
                        return dataObjectIdMatch || sourceMatch;
                    });

                    if (matchingJob) {
                        // Immediately set the job state (batch updates)
                        setCurrentJobId(matchingJob.id);
                        setJobWasFound(true);
                        setCurrentDataObjectName(dataObject.name);
                        setCurrentDataObjectUuid(dataObject.uuid || dataObject.id.toString());
                        setRetriggerAnalysis(false); // Never show retrigger for real jobs

                        // Start polling with the real job
                        asyncJob.startPolling(matchingJob.id);

                        // Add to watched objects
                        if (addWatchedObject) {
                            addWatchedObject(dataObject.uuid || dataObject.id.toString());
                        }
                    }

                } catch (error) {
                    console.error('🚀 [DIRECT_API] Failed to fetch job status:', error);
                }
            };

            // Execute immediately when modal opens
            fetchCurrentJobStatus();
        }
    }, [isOpen, dataObject?.id, dataObject?.status]); // Simplified dependencies

    // OPTIMIZED: Add effect to check for active jobs when modal opens
    // This handles cases where a job is running but modal doesn't know about it
    useEffect(() => {
        if (!isOpen || !dataObject || asyncJob.isPolling) return; // Skip if already polling

        // If data object is in processing state, try to find and reconnect to the running job
        if ((dataObject.status === 'processing' || dataObject.status === 'analyzing') &&
            runningJobs && runningJobs.length > 0) {

            // Look for a running job that matches this data object
            const matchingJob = runningJobs.find(job => {
                const dataObjectIdMatch = job.data_object_id === dataObject.uuid;
                const sourceMatch = job.source_identifier === dataObject.uuid ||
                    job.source_identifier === dataObject.name ||
                    job.source_identifier.includes(dataObject.name);

                return dataObjectIdMatch || sourceMatch;
            });

            if (matchingJob) {
                // Batch state updates to prevent multiple re-renders
                setCurrentDataObjectName(dataObject.name);
                setCurrentDataObjectUuid(dataObject.uuid || dataObject.id.toString());
                setCurrentJobId(matchingJob.id);
                setJobWasFound(true);
                setRetriggerAnalysis(false); // Critical: don't show retrigger state for active jobs

                // Add to watched objects
                if (addWatchedObject) {
                    addWatchedObject(dataObject.uuid || dataObject.id.toString());
                }

                // Start polling if not already polling this specific job
                if (!asyncJob.jobStatus || asyncJob.jobStatus.id !== matchingJob.id) {
                    asyncJob.startPolling(matchingJob.id);
                }
            }
        }
    }, [isOpen, dataObject?.id, dataObject?.status, runningJobs?.length, asyncJob.isPolling]); // Simplified dependencies

    // Load existing rules for the data object when entering edit metadata mode
    // NOTE: Consolidate both existing rules and available rules loading to avoid duplicate API calls
    useEffect(() => {
        let cancelled = false; // Flag to prevent state updates if component unmounts or effect re-runs

        const loadRules = async () => {
            // Only load rules when actively editing metadata
            if (!dataObject?.uuid || !editing) {
                if (!cancelled) {
                    setExistingRules([]);
                    setAllAvailableRules([]);
                    setRulesLoading(false);
                    setAllRulesLoading(false);
                }
                return;
            }

            // Only load once - use the same data for both purposes
            if (rulesLoading || allRulesLoading) return;

            if (!cancelled) {
                setRulesLoading(true);
                setAllRulesLoading(true);
            }

            try {
                const response = await dqRuleService.getRulesForDataObject(dataObject.uuid);

                // Prevent state updates if the effect was cancelled (React StrictMode protection)
                if (!cancelled) {
                    if (response.rules && response.rules.length > 0) {
                        // Use same data for both existing rules and available rules for reuse
                        setExistingRules(response.rules);
                        setAllAvailableRules(response.rules);
                    } else {
                        setExistingRules([]);
                        setAllAvailableRules([]);
                    }
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('[DataObjectActionsModal] Failed to load rules:', error);
                    setExistingRules([]);
                    setAllAvailableRules([]);
                    // Don't show error toast - this is not critical for the editing functionality
                }
            } finally {
                if (!cancelled) {
                    setRulesLoading(false);
                    setAllRulesLoading(false);
                }
            }
        };

        loadRules();

        // Cleanup function to prevent state updates if effect re-runs
        return () => {
            cancelled = true;
        };
    }, [dataObject?.uuid, editing]); // Load rules only when entering edit mode

    // Handle re-trigger analysis for Databricks tables
    // --- Schema check before retrigger ---
    const handlePreRetriggerSchemaCheck = async () => {
        if (!dataObject || !isDatabricksTable(dataObject)) return
        setLoadingRetrigger(true)
        try {
            const tableFullName = getDatabricksTableName(dataObject)
            // Fire schema comparison
            await schemaComparison.compareWithTable(dataObject.uuid, tableFullName)
            setShowSchemaModal(true)
        } finally {
            setLoadingRetrigger(false)
        }
    }

    const handleSchemaRetriggerProceed = () => {
        setShowSchemaModal(false)
        // Schema compatible → proceed with actual retrigger
        handleRetriggerAnalysis()
    }

    const handleSchemaRetriggerGoBack = () => {
        setShowSchemaModal(false)
        schemaComparison.reset()
    }

    const handleRetriggerAnalysis = async () => {
        if (!dataObject || !isDatabricksTable(dataObject)) return

        try {
            // ISOLATION: Ensure we're only affecting THIS specific data object


            // Clear any previous job state for THIS data object only
            if (currentJobId && asyncJob.isPolling) {

                asyncJob.stopPolling();
            }

            // Remove this data object from watched objects temporarily during retrigger
            if (removeWatchedObject && dataObject.uuid) {
                removeWatchedObject(dataObject.uuid);

            }

            setRetriggerAnalysis(true)
            setRetriggerWasClicked(true) // Mark that retrigger was clicked
            setJobWasFound(false) // Reset job found state for new analysis
            setAutoTriggeredStatus(false) // Reset auto-trigger state for new analysis


            const tableFullName = getDatabricksTableName(dataObject)

            // Find folder information for this data object
            const currentFolder = dataObject.folderId ? folders.find(f => f.id === dataObject.folderId) : undefined;
            const folderInfo = currentFolder ? {
                folderId: currentFolder.id,
                folderUuid: currentFolder.uuid,
                folderName: currentFolder.name
            } : undefined;

            // Start async analysis job — backend retrieves description from data_object_id
            const { jobId } = await reportService.startAsyncAnalysisTable(
                tableFullName,
                dataObject.name,
                dataObject.uuid,
                folderInfo,
                activeLanguage
            )


            // Update data object status to analyzing
            await dataObjectService.updateDataSource(dataObject.id, {
                status: 'analyzing',
                dataObjectUuid: dataObject.uuid
            })

            // Notify parent about the status change by creating an updated data source object
            if (onDataSourceUpdated) {
                const updatedDataSource = {
                    ...dataObject,
                    status: 'processing' as const // Frontend uses 'processing' for 'analyzing' state
                };
                onDataSourceUpdated(updatedDataSource);
            }

            // Start async analysis tracking - batch state updates to prevent multiple re-renders
            setCurrentDataObjectName(dataObject.name)
            setCurrentDataObjectUuid(dataObject.uuid)
            setCurrentJobId(jobId)
            setJobWasFound(true) // Mark that we now have a job

            // Register this object for selective polling
            if (addWatchedObject && dataObject.uuid) {
                addWatchedObject(dataObject.uuid);
            }

            // Note: Don't set retriggerAnalysis to false yet - let the job polling establish first
            asyncJob.startPolling(jobId)

            // Don't close modal or show toast - let async progress handle it

        } catch (error) {
            console.error('Failed to retrigger analysis:', error)
            setRetriggerAnalysis(false)
            setJobWasFound(false)
            setAutoTriggeredStatus(false)
            pushToast({
                variant: 'error',
                message: `${t('Failed to retrigger analysis.')} ${error?.response?.data?.detail || error?.message || t('Please try again.')}`
            })
        }
    }

    // Generate preview data from stored column sample values
    const generatePreviewData = (cols: FileColumn[]) => {
        // Build preview from sample values stored in column metadata
        const hasRealSampleData = cols.some(col => col.sampleValues && col.sampleValues.length > 0);

        if (!hasRealSampleData) {
            // Just show column structure without mock values to avoid confusion
            return cols.slice(0, 3).map((col) => {
                const row: { [key: string]: string } = {};
                row[col.name] = `[${col.detectedType}]`;
                return row;
            });
        }

        // Use sample values to build preview rows
        const maxRows = Math.max(...cols.map(c => c.sampleValues?.length || 0));
        const rows = [];
        for (let i = 0; i < Math.min(5, maxRows); i++) {
            const row: { [key: string]: string } = {};
            cols.forEach((col) => {
                if (col.sampleValues && col.sampleValues.length > i) {
                    row[col.name] = col.sampleValues[i];
                } else {
                    row[col.name] = `[${col.detectedType}]`;
                }
            });
            rows.push(row);
        }
        return rows;
    }

    // Create analysis object for the table components
    const hasRealSampleData = columns.some(col => col.sampleValues && col.sampleValues.length > 0);
    // Column search filtering (name and description)
    const filteredColumns = columnSearch.trim()
        ? columns.filter(col => (
            col.name.toLowerCase().includes(columnSearch.toLowerCase()) ||
            (col.description ? col.description.toLowerCase().includes(columnSearch.toLowerCase()) : false)
        ))
        : columns;

    const analysisObject = columns.length > 0 ? {
        fileName: dataObject?.name || '',
        fileSize: 0,
        columns: columns,
        recordCount: Math.max(...columns.map(c => c.sampleValues?.length || 0)) || 0,
        previewData: generatePreviewData(columns)
    } : null

    // Filtered analysis object for table rendering
    const filteredAnalysisObject = filteredColumns.length > 0 ? {
        fileName: dataObject?.name || '',
        fileSize: 0,
        columns: filteredColumns,
        recordCount: Math.max(...filteredColumns.map(c => c.sampleValues?.length || 0)) || 0,
        previewData: generatePreviewData(filteredColumns)
    } : null

    useEffect(() => {
        if (isOpen && dataObject) {
            // DataObject details available for processing
            setName(dataObject.name)
            setDescription((dataObject as any).description || '')
            setNameError('')
            setEditing(false)
            // Reset columns state but don't fetch API data yet
            setColumns([])
            setColumnsLoading(false)
            setColumnsError('')

            // Clear async job state if switching to a different data object
            if (currentDataObjectName && currentDataObjectName !== dataObject.name) {
                asyncJob.stopPolling()
                setCurrentDataObjectName(null)
                setCurrentDataObjectUuid(null)
                setCurrentJobId(null)
                setRetriggerAnalysis(false) // Reset retrigger state when switching
                setJobWasFound(false)
                setAutoTriggeredStatus(false) // Reset auto-trigger state when switching to different object
            }

            // Check if this data object is currently processing and resume progress tracking
            // Only check for jobs if the card is actually in a processing state OR explicitly flagged for job checking
            const isProcessingState = dataObject.status === 'processing' || dataObject.status === 'analyzing';
            const shouldCheckForJob = (dataObject as any).shouldCheckForJob;

            // CRITICAL FIX: If data object is "processed" but we have stale job state, clean it up
            // BUT NOT if we just started a retrigger operation (retriggerAnalysis=true)
            const isCompletedState = dataObject.status === 'processed';
            const hasStaleJobState = asyncJob.jobStatus || currentJobId || jobWasFound;
            if (isCompletedState && hasStaleJobState && !retriggerAnalysis) {

                asyncJob.stopPolling();
                setCurrentDataObjectName(null);
                setCurrentDataObjectUuid(null);
                setCurrentJobId(null);
                setRetriggerAnalysis(false);
                setJobWasFound(false);
                setAutoTriggeredStatus(false);
                // Don't proceed with job checking for completed objects
                return;
            }

            if ((isProcessingState || shouldCheckForJob) && (dataObject as any).uuid) {
                // Only check for job if we haven't already started checking for this specific data object
                if (currentDataObjectUuid !== (dataObject as any).uuid) {
                    // Auto-trigger status check for processing/analyzing data objects
                    // BUT don't reset if we're reopening the same object that already has job status
                    const shouldAutoTrigger = isProcessingState && !autoTriggeredStatus && (
                        currentDataObjectName !== dataObject.name ||
                        !asyncJob.jobStatus
                    );

                    if (shouldAutoTrigger) {
                        setAutoTriggeredStatus(true);
                    }

                    // Always check for existing running job to restore proper state
                    let matchingJob: JobStatus | undefined;

                    if (getJobForDataObject) {
                        matchingJob = getJobForDataObject(dataObject.uuid || dataObject.id.toString());
                    } else {
                        // Fallback: search through running jobs manually
                        matchingJob = runningJobs.find(job => {
                            const sourceMatch = job.source_identifier === (dataObject as any).uuid ||
                                job.source_identifier === dataObject.name ||
                                job.source_identifier.includes(dataObject.name)
                            const nameMatch = job.dataset_name === dataObject.name
                            return sourceMatch || nameMatch
                        })
                    }

                    if (matchingJob) {
                        // Found existing job - immediately restore full state
                        setJobWasFound(true)
                        setRetriggerAnalysis(false)
                        setCurrentDataObjectName(dataObject.name)
                        setCurrentDataObjectUuid((dataObject as any).uuid)
                        setCurrentJobId(matchingJob.id)

                        // Immediately start polling to get latest status
                        asyncJob.startPolling(matchingJob.id)
                    } else {
                        // No job found - only set retrigger state if card is actually in processing state
                        setJobWasFound(false)
                        setCurrentJobId(null)

                        // Only show retrigger/analyzing state if the card status indicates processing
                        if (dataObject.status === 'processing' || dataObject.status === 'analyzing') {
                            setRetriggerAnalysis(true)
                            checkForExistingJob(dataObject)
                        } else {
                            // Card is analyzed/complete - clear all job state completely
                            setRetriggerAnalysis(false)
                            setJobWasFound(false)
                            setCurrentJobId(null)
                            setCurrentDataObjectName(null)
                            setCurrentDataObjectUuid(null)
                            setAutoTriggeredStatus(false)

                            // Also clear async job state for completed analysis
                            asyncJob.stopPolling()

                            // Analysis complete - job states cleared
                        }
                    }
                } else {
                    // Same data object - just ensure job tracking is active
                    if (currentJobId) {
                        // Ensure we're still polling the current job
                        asyncJob.startPolling(currentJobId);
                    }
                }
            } else if (!asyncJob.isPolling && !asyncJob.jobStatus) {
                // Clear any lingering job state only if there's no job status
                setCurrentDataObjectName(null)
                setCurrentDataObjectUuid(null)
                setCurrentJobId(null)
                setRetriggerAnalysis(false)
                setJobWasFound(false)
                setAutoTriggeredStatus(false) // Reset auto-trigger state
            }
        }

        // Setup seamless auto-refresh for accurate status sync (only if no active job polling)
        if (isOpen && dataObject && !asyncJob.isPolling) {
            // Initial refresh
            refreshJobStatus()

            // Setup interval for seamless background refresh (every 10 seconds to reduce flicker)
            const interval = window.setInterval(() => {
                if (!asyncJob.isPolling) { // Only refresh if not actively polling
                    refreshJobStatus()
                }
            }, 10000) // Increased to 10 seconds to reduce UI updates
            setRefreshInterval(interval)
        }

        // Cleanup when modal closes
        return () => {
            if (refreshInterval) {
                if (refreshInterval !== null) {
                    window.clearInterval(refreshInterval)
                }
                setRefreshInterval(null)
            }

            if (!isOpen) {
                // Keep all state when closing modal - only pause polling to save resources
                // The state will be restored when modal reopens
                // Note: Don't call asyncJob.stopPolling() as it clears the job status
            }
        }
    }, [isOpen, dataObject, currentDataObjectName, currentDataObjectUuid])

    // Function to check for existing processing jobs and resume tracking (with retry capability)
    const checkForExistingJob = async (dataObject: DataSource, retryCount = 0) => {
        const maxRetries = 3

        // If we have a direct job ID from file replace, use it immediately
        if ((dataObject as any).jobId) {
            setCurrentDataObjectName(dataObject.name)
            setCurrentDataObjectUuid((dataObject as any).uuid)
            asyncJob.startPolling((dataObject as any).jobId)
            return
        }

        // Use the getJobForDataObject function if available, otherwise search through running jobs
        let matchingJob: JobStatus | undefined;

        if (getJobForDataObject) {
            matchingJob = getJobForDataObject(dataObject.uuid || dataObject.id.toString());
        }

        if (!matchingJob && runningJobs.length > 0) {
            // Fallback: search through running jobs manually
            matchingJob = runningJobs.find(job => {
                // Primary match: data_object_id should match dataObject.uuid
                const dataObjectIdMatch = job.data_object_id === dataObject.uuid;

                // Fallback matches for backward compatibility
                const sourceMatch = job.source_identifier === (dataObject as any).uuid ||
                    job.source_identifier === dataObject.name ||
                    job.source_identifier.includes(dataObject.name)
                const nameMatch = job.dataset_name === dataObject.name

                return dataObjectIdMatch || sourceMatch || nameMatch
            })
        }

        // If no local match, fetch directly from the API (critical for page refresh scenario)
        if (!matchingJob) {
            try {
                const apiJobs = await asyncDataDiagnosisService.getRunningJobs();
                matchingJob = apiJobs.find(job => {
                    const dataObjectIdMatch = job.data_object_id === dataObject.uuid;
                    const sourceMatch = job.source_identifier === (dataObject as any).uuid ||
                        job.source_identifier === dataObject.name ||
                        job.source_identifier?.includes(dataObject.name);
                    return dataObjectIdMatch || sourceMatch;
                });
            } catch {
                // API call failed — will retry below if applicable
            }
        }

        if (matchingJob) {
            // Found an active job - resume progress tracking
            setCurrentDataObjectName(dataObject.name)
            setCurrentDataObjectUuid((dataObject as any).uuid)
            setCurrentJobId(matchingJob.id)
            setJobWasFound(true) // Mark that we found an existing job
            setRetriggerAnalysis(false) // Clear retrigger state since job is already running
            asyncJob.startPolling(matchingJob.id)

            // Add to watched objects for continued tracking
            if (addWatchedObject) {
                addWatchedObject(dataObject.uuid || dataObject.id.toString());
            }
        } else if (retryCount < maxRetries) {
            // Retry for any processing data object, not just those with shouldCheckForJob
            setTimeout(() => {
                checkForExistingJob(dataObject, retryCount + 1)
            }, 3000)
        }
    }

    // Extract columns from data_description - fetch from API with metadata only when editing
    const fetchMetadata = async () => {
        setColumnsLoading(true)
        setColumnsError('')
        setColumns([])

        try {
            // Use the real UUID if available, fall back to numeric ID
            const objectUuid = (dataObject as any).uuid;
            const idToUse = objectUuid || dataObject?.id;

            // Single API call: pass target_language when non-English so the backend
            // returns the translated data_description in one round-trip.
            const targetLang = (activeLanguage && activeLanguage !== 'en') ? activeLanguage : undefined;
            const dataObjectWithMetadata = await dataObjectService.getDataSource(idToUse, true, targetLang);

            // Check multiple possible locations for column metadata
            let dataDescObj = (dataObjectWithMetadata as any).data_description;
            let extraMetadata = (dataObjectWithMetadata as any).extra_metadata;



            // Extract description from metadata if available
            let extractedDescription = '';
            if (dataDescObj?.description) {
                extractedDescription = dataDescObj.description;
            } else if (extraMetadata?.data_description?.description) {
                extractedDescription = extraMetadata.data_description.description;
            } else if ((dataObjectWithMetadata as any).description) {
                extractedDescription = (dataObjectWithMetadata as any).description;
            }

            // Update description if we found one
            if (extractedDescription) {
                setDescription(extractedDescription);
                originalDescriptionRef.current = extractedDescription;
            }

            // Try different possible locations for column data
            let columns = null;
            let columnSource = 'not found';

            if (dataDescObj?.columns && Array.isArray(dataDescObj.columns)) {
                columns = dataDescObj.columns;
                columnSource = 'dataDescObj.columns';
            } else if (extraMetadata?.data_description?.columns && Array.isArray(extraMetadata.data_description.columns)) {
                columns = extraMetadata.data_description.columns;
                columnSource = 'extraMetadata.data_description.columns';
            } else if (extraMetadata?.columns && Array.isArray(extraMetadata.columns)) {
                columns = extraMetadata.columns;
                columnSource = 'extraMetadata.columns';
            }



            if (columns) {
                // Convert columns to FileColumn format  
                const extractedColumns: FileColumn[] = columns.map((col: any) => {

                    // Get sample values from stored column metadata
                    let sampleValues: string[] = [];

                    // Try to get sample values from column metadata (snake_case)
                    if (col.sample_values && Array.isArray(col.sample_values)) {
                        sampleValues = col.sample_values.map((v: any) => v?.toString() || '').slice(0, 5);
                    }

                    // Fallback: check for camelCase sampleValues in case it's already transformed
                    if (sampleValues.length === 0 && col.sampleValues && Array.isArray(col.sampleValues)) {
                        sampleValues = col.sampleValues.map((v: any) => v?.toString() || '').slice(0, 5);
                    }

                    return {
                        name: col.name,
                        detectedType: normalizeDataType(col.type || col.logical_type || col.data_type || 'String'),
                        selectedType: normalizeDataType(col.type || col.logical_type || col.data_type || 'String'),
                        description: col.description || '',
                        sampleValues: sampleValues,
                        hasQualityIssues: col.has_quality_issues ?? false,
                        analysis_flag: col.analysis_flag ?? true, // Preserve backend value, default to true
                        // Store DQ rules information for potential future use
                        userCreatedRules: col.user_created_rules || [],
                        allDqRules: col.all_dq_rules || []
                    };
                })



                setColumns(extractedColumns)
                originalColumnsRef.current = extractedColumns
                // Load DQ rules indicators from the column metadata (no separate API call needed)
                loadColumnRulesFromMetadata(columns);
            } else {
                // Provide different messages based on status
                let errorMessage = t('Column metadata is not yet available.');
                if (dataObjectWithMetadata.status === 'processing') {
                    errorMessage = t('File is currently being processed and analyzed. Column metadata will be available once processing is complete.');
                } else if (dataObjectWithMetadata.status === 'error') {
                    errorMessage = t('There was an error processing this file. Column metadata could not be generated.');
                } else {
                    errorMessage = t('Column metadata is not yet available. The file may need to be reprocessed to generate column information.');
                }

                // No column data available
                setColumns([])
                setColumnsError(errorMessage)
            }
        } catch (error) {
            console.error('[DataObjectActionsModal] Failed to fetch metadata:', error);
            setColumnsError(t('Failed to load column metadata. Please try again later.'))
            setColumns([])
        } finally {
            setColumnsLoading(false)
        }
    }

    // Load DQ rules count for each column from the column metadata (no separate API call needed)
    const loadColumnRulesFromMetadata = (columns: any[]) => {
        if (!columns) return;

        try {
            const rulesByColumn: Record<string, number> = {};

            // Count active user_created_rules per column from metadata
            columns.forEach(column => {
                if (column.user_created_rules && Array.isArray(column.user_created_rules)) {
                    const activeRules = column.user_created_rules.filter(rule => rule.is_active === true);
                    if (activeRules.length > 0) {
                        rulesByColumn[column.name] = activeRules.length;
                    }
                }
            });

            setColumnRules(rulesByColumn);
        } catch (error) {
            console.error('[DataObjectActionsModal] Failed to extract DQ rules from metadata:', error);
            setColumnRules({});
        }
    }

    // Translate metadata in-place using the inline GET with target_language
    const applyMetadataTranslation = async (
        objectUuid: string,
        targetLanguage: string,
        currentDescription: string,
        currentColumns: FileColumn[]
    ) => {
        setTranslatingMetadata(true)
        try {
            const response = await dataObjectService.getDataSource(objectUuid, true, targetLanguage)
            const translated = (response as any).data_description

            if (!translated) {
                return
            }

            // Overlay translated file-level description
            if (translated.description) {
                setDescription(translated.description)
            }

            // Overlay translated per-column descriptions
            if (translated.column_description && currentColumns.length > 0) {
                setColumns(prev =>
                    prev.map(col => {
                        const translatedCol = translated.column_description[col.name]
                        if (translatedCol) {
                            return { ...col, description: translatedCol.description }
                        }
                        return col
                    })
                )
            }
        } catch (error) {
            console.error('[DataObjectActionsModal] Translation failed:', error)
            pushToast({ variant: 'warning', message: t('Could not translate metadata. Showing original.') })
        } finally {
            setTranslatingMetadata(false)
        }
    }

    // React to global language changes while the editing view is open
    useEffect(() => {
        if (!editing || columns.length === 0 || !dataObject?.uuid) return

        if (activeLanguage === 'en') {
            // Restore originals when switching back to English
            if (originalDescriptionRef.current) {
                setDescription(originalDescriptionRef.current)
            }
            if (originalColumnsRef.current.length > 0) {
                setColumns(originalColumnsRef.current)
            }
        } else {
            applyMetadataTranslation(
                dataObject.uuid,
                activeLanguage,
                originalDescriptionRef.current,
                originalColumnsRef.current
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLanguage])

    // Enhanced close handler that refreshes page if retrigger was clicked
    const handleClose = useCallback(() => {
        // ISOLATION: Clean up this data object's state when modal closes
        if (currentDataObjectUuid && removeWatchedObject && !retriggerWasClicked) {

            removeWatchedObject(currentDataObjectUuid);
        }

        // Stop any polling for this specific data object when modal closes
        if (asyncJob.isPolling && !retriggerWasClicked) {

            asyncJob.stopPolling();
        }

        if (retriggerWasClicked) {
            // Refresh the entire page when closing after retrigger
            // This ensures fresh data is loaded regardless of current view
            window.location.reload()
        } else {
            onClose()
        }
    }, [retriggerWasClicked, onClose, currentDataObjectUuid, removeWatchedObject, asyncJob])

    // Reset retrigger clicked state when modal opens
    useEffect(() => {
        if (isOpen) {
            setRetriggerWasClicked(false)
        }
    }, [isOpen])

    // Simple escape key handling without focus trap
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (editing) {
                    setEditing(false)
                    setNameError('')
                } else {
                    handleClose()
                }
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            return () => document.removeEventListener('keydown', handleKeyDown)
        }
    }, [isOpen, editing, handleClose])

    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            // Store current scroll position
            const scrollY = window.scrollY;
            // Prevent body scroll
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';

            return () => {
                // Restore body scroll and position
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                // Restore scroll position
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen]);

    // Stable handler for column updates to avoid conditional hooks
    const handleColumnUpdate = useCallback(async (columnName: string, field: keyof FileColumn, value: string) => {
        // Update local state immediately for responsive UI
        setColumns(prev => prev.map(col =>
            col.name === columnName ? { ...col, [field]: value } : col
        ))

        // NOTE: Column updates are currently local-only
        // Backend column updates are deprecated; full data_description is patched elsewhere
    }, [setColumns])

    if (!isOpen || !dataObject) return null

    const handleSave = async () => {


        if (!name.trim()) {
            setNameError(t('Name is required'))
            return
        }

        setSaving(true)
        setNameError('') // Clear any previous errors

        try {
            const currentDescription = (dataObject as any).description || ''
            const hasNameChange = name !== dataObject.name
            const hasDescriptionChange = description !== currentDescription
            const hasColumns = columns.length > 0

            // Clean columns data - remove rule modifications that are only local to avoid backend issues
            // Sync analysis_flag from column selection checkboxes
            const cleanColumns = hasColumns ? columns.map(col => {
                const cleanCol = { ...col };
                // Only include basic column metadata, not locally modified rules
                delete cleanCol.rules; // Rules are handled separately via DQ rules API
                // Update analysis_flag based on user's column selection
                if (selectedColumnsForReport.length > 0) {
                    cleanCol.analysis_flag = selectedColumnsForReport.includes(col.name);
                }
                return cleanCol;
            }) : undefined;



            // Update metadata including name, description, and columns (without rule modifications)
            if (hasNameChange || hasDescriptionChange || hasColumns) {
                await onUpdateMetadata(dataObject.id, {
                    name: name.trim(),
                    description: description.trim(),
                    // Pass the real UUID to avoid fake UUID generation issues
                    dataObjectUuid: (dataObject as any).uuid,
                    // Include clean columns state without rule modifications
                    columns: cleanColumns,
                    // Pass current language for inline translation
                    targetLanguage: activeLanguage
                })
            }
            setEditing(false)

            // Close the modal after successful save instead of refreshing the page
            onClose()
        } catch (error) {
            console.error('[DataObjectActionsModal] Failed to save metadata:', error);
            setNameError(t('Failed to save changes. Please try again.'))
        } finally {
            setSaving(false)
        }
    }

    // View Report button disabled when job is actively running

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-auto"
            onMouseDown={() => { }} // Disabled backdrop click to prevent accidental closes
            role="dialog"
            aria-modal="true"
            aria-labelledby="data-object-actions-title"
        >
            <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-gray-900/50 to-black/70 backdrop-blur-md" />
            <div
                ref={dialogRef}
                className={`relative rounded-3xl border border-gray-200/50 shadow-2xl overflow-hidden transform transition-all duration-300 flex flex-col my-auto ${editing ? 'modal-content modal-size-full gap-0' : 'w-full max-w-2xl gap-1 sm:gap-3 max-h-[92vh] overflow-y-auto'}`}
                style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)'
                }}
            >
                {/* Header with red gradient theme */}
                <div className={editing ? "bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-6 py-3 flex-shrink-0" : "bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-4 py-3 sm:px-8 sm:py-6"}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            {/* Data Object Icon */}
                            <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center">
                                <VscGithubAction className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                            </div>
                            <div>
                                {editing ? (
                                    <h2 className="text-2xl font-bold text-white tracking-tight">
                                        {t('Edit Data Object Metadata')}
                                    </h2>
                                ) : (
                                    <>
                                        <h2 className="text-lg sm:text-2xl font-bold text-white tracking-tight">
                                            {t('Data Object Actions')}
                                        </h2>
                                        <p className="text-red-100 text-xs sm:text-sm font-medium hidden sm:block">
                                            {isDatabricksTable(dataObject)
                                                ? t('Choose an action for this Databricks table')
                                                : t('Choose an action for this data object')}
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <ContextualHelpLink videoId="tv-005" variant="icon-light" />
                            <button
                                onClick={() => {
                                    if (editing) {
                                        setEditing(false)
                                        setNameError('')
                                    } else {
                                        handleClose()
                                    }
                                }}
                                className="w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-white transition-all duration-200 hover:scale-105"
                                aria-label="Close"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className={editing ? "px-4 py-2 flex-1 overflow-hidden min-h-0" : "px-4 py-3 sm:px-8 sm:py-6"}>

                    {/* Progress Section - Additional section when analysis is running */}
                    {(() => {
                        // Only show progress if THIS specific data object has an active job
                        const isDataObjectInProgress = dataObject.status === 'processing' || dataObject.status === 'analyzing'

                        // CRITICAL FIX: For processing data objects, NEVER show "retrigger" state
                        // Always prioritize real background jobs over retrigger state
                        const hasRealBackgroundJob = isDataObjectInProgress && runningJobs && runningJobs.length > 0
                        const isStartingAnalysis = retriggerAnalysis && !asyncJob.isComplete && !hasRealBackgroundJob

                        const hasActiveAsyncJob = asyncJob.jobStatus && !asyncJob.isComplete && asyncJob.isPolling
                        const hasJobIdActive = currentJobId && asyncJob.isPolling && !asyncJob.isComplete

                        // FIXED: Show progress if async job is active OR data object is in progress
                        // This handles cases where async job is running but data object status hasn't updated yet
                        const showProgress = isDataObjectInProgress ||
                            isStartingAnalysis ||
                            hasActiveAsyncJob ||
                            hasJobIdActive ||
                            hasRealBackgroundJob

                        // Show progress when analysis is running
                        return showProgress
                    })() && (
                            <div className="mb-3 sm:mb-6">
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-6">
                                    <div className="text-center">
                                        {/* Progress Title - NEVER show "Re-triggering" for processing data objects */}
                                        <h3 className="text-sm sm:text-lg font-semibold text-red-700 mb-1 sm:mb-2">
                                            {(() => {
                                                const isDataObjectInProgress = dataObject.status === 'processing' || dataObject.status === 'analyzing'
                                                const hasRealBackgroundJob = isDataObjectInProgress && runningJobs && runningJobs.length > 0
                                                return (hasRealBackgroundJob || asyncJob.jobStatus || jobWasFound || asyncJob.isPolling || isDataObjectInProgress) ?
                                                    t('Analysis In Progress') : t('Re-triggering Analysis')
                                            })()}
                                        </h3>                                    {/* Progress Description */}
                                        <p className="text-xs sm:text-sm text-red-700 mb-2 sm:mb-4">
                                            {currentDataObjectName ? <span>{t('Analyzing')} <span className="notranslate">{currentDataObjectName}</span></span> : t('Analyzing your data')}
                                        </p>

                                        {/* Always show progress bar for processing/analyzing status */}
                                        <div className="space-y-3" aria-live="polite">
                                            {/* Progress Bar */}
                                            <div className="w-full bg-red-100 rounded-full h-2 overflow-hidden notranslate" translate="no">
                                                {(() => {
                                                    // Priority: Real-time asyncJob progress > default to 0% for starting jobs
                                                    let displayProgress = 0;

                                                    if (asyncJob.jobStatus && asyncJob.progress !== undefined) {
                                                        // Use real-time progress from async job
                                                        displayProgress = asyncJob.progress;
                                                    } else if (currentJobId && asyncJob.isPolling && !asyncJob.isComplete) {
                                                        // Job exists but not yet with progress - show some minimal progress
                                                        displayProgress = 5;
                                                    } else if (retriggerAnalysis && !asyncJob.isComplete && !asyncJob.jobStatus) {
                                                        // Just starting - show minimal progress
                                                        displayProgress = 0;
                                                    }

                                                    return (
                                                        <div
                                                            className={`h-2 rounded-full transition-all duration-300 ${displayProgress >= 100 ? 'bg-green-500' : 'bg-red-500'
                                                                }`}
                                                            style={{ width: `${displayProgress}%` }}
                                                        />
                                                    );
                                                })()}
                                            </div>

                                            {/* Progress Text */}
                                            <div className="text-center">
                                                <div className="text-sm font-medium text-red-700">
                                                    {(() => {
                                                        // Use same progress logic as progress bar
                                                        let displayProgress = 0;
                                                        let displayPhase = '';

                                                        if (asyncJob.jobStatus && asyncJob.progress !== undefined) {
                                                            displayProgress = asyncJob.progress;
                                                            displayPhase = asyncJob.currentPhase || '';

                                                            // Special handling when complete
                                                            if (displayProgress >= 100) {
                                                                return (
                                                                    <>
                                                                        <span className="text-green-700 font-semibold">✅ {t('Analysis Complete!')}</span>
                                                                        <div className="text-xs text-green-600 mt-1">{t('Report is ready to view')}</div>
                                                                    </>
                                                                );
                                                            }
                                                        } else if (currentJobId && asyncJob.isPolling && !asyncJob.isComplete) {
                                                            displayProgress = 5;
                                                            displayPhase = t('Starting...');
                                                        } else if (retriggerAnalysis && !asyncJob.isComplete && !asyncJob.jobStatus) {
                                                            displayProgress = 0;
                                                            displayPhase = t('Initializing...');
                                                        }

                                                        return (
                                                            <>
                                                                <span className="notranslate">{displayProgress.toFixed(0)}%</span> {t('Complete')}
                                                                {displayPhase && (
                                                                    <span className="ml-2 text-xs">({displayPhase})</span>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                                {asyncJob.jobStatus?.error_message && (
                                                    <div className="text-xs text-red-600 mt-1">
                                                        {asyncJob.jobStatus.error_message}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Status Details */}
                                            {asyncJob.jobStatus?.status && (
                                                <div className="text-xs text-red-600 text-center">
                                                    Status: {asyncJob.jobStatus.status} • Phase: {asyncJob.jobStatus.current_phase}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    {!editing && (
                        <div className="space-y-3 sm:space-y-6">
                            {/* Data Object Preview Card */}
                            <div className="bg-gray-50 rounded-lg p-2.5 sm:p-4 border border-gray-200">
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center flex-shrink-0">
                                        <FileIcon
                                            fileName={dataObject.name}
                                            fileType={dataObject.type}
                                            className="w-8 h-8 sm:w-12 sm:h-12"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        {isDatabricksTable(dataObject) ? (
                                            <div>
                                                <h4 className="font-semibold text-black truncate">
                                                    {(() => {
                                                        const tableInfo = getTableInformation(dataObject);
                                                        return <span className="notranslate">{tableInfo.table}</span>;
                                                    })()}
                                                </h4>
                                                <div className="text-xs text-gray-600 mt-0.5">
                                                    {(() => {
                                                        const tableInfo = getTableInformation(dataObject);
                                                        return (
                                                            <>
                                                                <span className="font-semibold text-gray-800">{t('Catalog:')}</span> <span className="notranslate">{tableInfo.catalog}</span>
                                                                <span className="mx-2">•</span>
                                                                <span className="font-semibold text-gray-800">{t('Schema:')}</span> <span className="notranslate">{tableInfo.schema}</span>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        ) : (
                                            <h4 className="font-semibold text-black truncate notranslate">{dataObject.name}</h4>
                                        )}
                                        <div className="flex items-center gap-3 mt-1">
                                            <span className="text-sm text-black capitalize notranslate">
                                                {dataObject.type?.toLowerCase() === 'databricks'
                                                    ? 'Databricks'
                                                    : dataObject.type?.toUpperCase()}
                                            </span>
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                // Show processing status when retrigger is active or async job is polling
                                                (retriggerAnalysis || asyncJob.isPolling)
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : dataObject.status === 'processed'
                                                        ? 'bg-green-100 text-green-800'
                                                        : dataObject.status === 'processing'
                                                            ? 'bg-yellow-100 text-yellow-800'
                                                            : 'bg-red-100 text-red-800'
                                                }${
                                                // Hide the entire span if text would be empty
                                                (retriggerAnalysis || asyncJob.isPolling) || dataObject.status === 'processing'
                                                    ? ' hidden'
                                                    : ''
                                                }`}>
                                                {(retriggerAnalysis || asyncJob.isPolling)
                                                    ? ''
                                                    : dataObject.status === 'processed' ? t('Analyzed') :
                                                        dataObject.status === 'processing' ? '' :
                                                            dataObject.status ? (dataObject.status.charAt(0).toUpperCase() + dataObject.status.slice(1)) : t('Unknown')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Primary Action - Most Common Use Case (View report) */}
                            <div>
                                <button
                                    ref={firstButtonRef}
                                    type="button"
                                    onClick={() => { onViewReport(dataObject); handleClose() }}
                                    disabled={hasJobForThisDataObject}
                                    title={
                                        hasJobForThisDataObject
                                            ? 'Report generation in progress...'
                                            : 'View data analysis report'
                                    }
                                    className={`w-full rounded-lg px-4 py-2.5 sm:px-6 sm:py-4 font-medium transition-colors flex items-center justify-center gap-2 sm:gap-3 shadow-sm ${hasJobForThisDataObject
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'text-white hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 ease-out transform hover:shadow-lg'
                                        }`}
                                    style={hasJobForThisDataObject ? {} : {
                                        background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%)',
                                        border: 'none',
                                        boxShadow: '0 4px 15px rgba(220, 38, 38, 0.3)'
                                    }}
                                >
                                    <BiSolidReport className="w-6 h-6 sm:w-8 sm:h-8" />
                                    {t('View Report')}
                                </button>
                            </div>

                            {/* Secondary Actions Grid (3-column layout) */}
                            <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                {/* Upload new file */}
                                {isDatabricksTable(dataObject) ? (
                                    <button
                                        type="button"
                                        onClick={handlePreRetriggerSchemaCheck}
                                        disabled={hasJobForProgressDisplay || loadingRetrigger}
                                        title={hasJobForProgressDisplay ? 'Analysis in progress…' : loadingRetrigger ? 'Loading…' : 'Re-trigger analysis'}
                                        aria-disabled={hasJobForProgressDisplay || loadingRetrigger}
                                        className={`flex flex-col items-center justify-center p-1.5 sm:p-3 rounded-lg border-2 transition-colors text-center group focus:outline-none focus:ring-2 focus:ring-brand-500 ${hasJobForProgressDisplay || loadingRetrigger
                                            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                            : 'border-gray-200 hover:border-red-300 hover:bg-red-50'}`}
                                    >
                                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-100 rounded-lg flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-red-200 transition-colors">
                                            {hasJobForProgressDisplay || loadingRetrigger ? (
                                                <div className="loader-red loader-sm"></div>
                                            ) : (
                                                <GrCycle className="w-4 h-4 sm:w-8 sm:h-8 text-red-600" />
                                            )}
                                        </div>
                                        <span className="font-medium text-xs sm:text-sm text-black">
                                            {hasJobForProgressDisplay ? t('Analyzing...') : loadingRetrigger ? t('Loading...') : t('Re-trigger')}
                                        </span>
                                        <span className="text-[10px] sm:text-xs text-black mt-0.5 sm:mt-1 hidden sm:block">
                                            {hasJobForProgressDisplay ? t('Please wait...') : loadingRetrigger ? t('Checking schema...') : t('Run analysis again')}
                                        </span>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (!hasJobForProgressDisplay && !loadingReplace) {
                                                setLoadingReplace(true)
                                                try {
                                                    await onUploadFile(dataObject)
                                                } finally {
                                                    setLoadingReplace(false)
                                                }
                                            }
                                        }}
                                        disabled={hasJobForProgressDisplay || loadingReplace}
                                        title={hasJobForProgressDisplay ? 'Analysis in progress…' : loadingReplace ? 'Loading…' : 'Upload new file'}
                                        aria-disabled={hasJobForProgressDisplay || loadingReplace}
                                        className={`flex flex-col items-center justify-center p-1.5 sm:p-3 rounded-lg border-2 transition-colors text-center group focus:outline-none focus:ring-2 focus:ring-brand-500 ${hasJobForProgressDisplay || loadingReplace
                                            ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                            : 'border-gray-200 hover:border-red-300 hover:bg-red-50'}`}
                                    >
                                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-red-100 rounded-lg flex items-center justify-center mb-1 group-hover:bg-red-200 transition-colors">
                                            {loadingReplace ? (
                                                <div className="loader-red loader-sm"></div>
                                            ) : (
                                                <IoMdCloudUpload className="w-4 h-4 sm:w-8 sm:h-8 text-red-600" />
                                            )}
                                        </div>
                                        <span className="font-medium text-xs sm:text-sm text-black">{loadingReplace ? t('Loading...') : t('Upload File')}</span>
                                        <span className="text-[10px] sm:text-xs text-black mt-0.5 hidden sm:block">{hasJobForProgressDisplay ? t('Processing…') : loadingReplace ? t('Opening...') : t('Replace current file')}</span>
                                    </button>
                                )}

                                <button
                                    type="button"
                                    onClick={() => {
                                        setEditing(true)
                                        fetchMetadata()
                                    }}
                                    className="flex flex-col items-center justify-center p-1.5 sm:p-4 rounded-lg border-2 border-gray-200 hover:border-brand-300 hover:bg-brand-50 transition-colors text-center group focus:outline-none focus:ring-2 focus:ring-brand-500"
                                >
                                    <div className="w-6 h-6 sm:w-10 sm:h-10 bg-red-100 rounded-lg flex items-center justify-center mb-1 sm:mb-2 group-hover:bg-red-200 transition-colors">
                                        <FaEdit className="w-4 h-4 sm:w-8 sm:h-8 text-red-600" />
                                    </div>
                                    <span className="font-medium text-xs sm:text-sm text-black">{t('Edit Metadata')}</span>
                                    <span className="text-[10px] sm:text-xs text-black mt-0.5 sm:mt-1 hidden sm:block">{t('Edit name & description')}</span>
                                </button>

                                {/* Always show Move button; disable if no folders and object already unassigned */}
                                <button
                                    type="button"
                                    disabled={folders.length === 0 && dataObject.folderId === undefined}
                                    onClick={() => onMoveRequest(dataObject)}
                                    className={`flex flex-col items-center justify-center p-1.5 sm:p-4 rounded-lg border-2 text-center group focus:outline-none focus:ring-2 focus:ring-brand-500 transition-colors ${folders.length === 0 && dataObject.folderId === undefined
                                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                        : 'border-gray-200 hover:border-brand-300 hover:bg-brand-50'}`}
                                >
                                    <div className={`w-6 h-6 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center mb-1 sm:mb-2 transition-colors ${folders.length === 0 && dataObject.folderId === undefined ? 'bg-gray-200' : 'bg-red-100 group-hover:bg-red-200'}`}>
                                        <FaAngleDoubleRight className={`w-4 h-4 sm:w-8 sm:h-8 ${folders.length === 0 && dataObject.folderId === undefined ? 'text-black' : 'text-red-600'}`} />
                                    </div>
                                    <span className="font-medium text-xs sm:text-sm text-black">{t('Move')}</span>
                                    <span className="text-[10px] sm:text-xs text-black mt-0.5 sm:mt-1 hidden sm:block">{t('Move to folder')}</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {editing && (
                        <div className="flex-1 flex flex-col h-full relative">
                            {translatingMetadata && (
                                <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center rounded-lg">
                                    <Loading message={t('Translating metadata…')} />
                                </div>
                            )}
                            <DataObjectConfig
                                dataObjectName={name}
                                dataObjectDescription={description}
                                nameError={nameError}
                                onNameChange={(value) => {
                                    setName(value)
                                    if (nameError && value.trim()) setNameError('')
                                }}
                                onDescriptionChange={setDescription}
                                nameInputRef={nameInputRef}
                                descriptionRef={descriptionRef}
                                lastUserFocusedRef={{ current: null }}
                                tableName={dataObject?.type?.toLowerCase() === 'databricks' ? dataObject?.name : undefined}
                                analysis={{
                                    fileName: dataObject?.name || 'untitled',
                                    fileSize: 0,
                                    recordCount: dataObject?.recordCount || 0,
                                    columns: columns.map(col => ({
                                        name: col.name,
                                        detectedType: col.detectedType || 'String',
                                        selectedType: col.selectedType || col.detectedType || 'String',
                                        description: col.description || '',
                                        sampleValues: col.sampleValues || [],
                                        rules: col.rules || '',
                                        analysis_flag: col.analysis_flag,
                                        allDqRules: col.allDqRules || []
                                    })),
                                    metadata: { rowCount: 0, fileSize: 0 },
                                    allDqRules: (() => {
                                        // Merge rules from API (this data object) with per-column rules
                                        // (includes cross-data-object public rules from backend metadata)
                                        const perColumnRules = columns.flatMap(col => col.allDqRules || []);
                                        const merged = [...allAvailableRules, ...perColumnRules];
                                        const seen = new Set<string>();
                                        return merged.filter(rule => {
                                            const id = rule.id;
                                            if (!id || seen.has(id)) return false;
                                            seen.add(id);
                                            return true;
                                        });
                                    })()
                                }}
                                onColumnUpdate={(columnName, field, value) => {
                                    // Handle column updates for the edit screen
                                    if (field === 'description') {
                                        setColumns(prev => prev.map(col =>
                                            col.name === columnName
                                                ? { ...col, description: value as string }
                                                : col
                                        ))
                                    } else if (field === 'selectedType') {
                                        setColumns(prev => prev.map(col =>
                                            col.name === columnName
                                                ? { ...col, selectedType: value as FileColumn['selectedType'] }
                                                : col
                                        ))
                                    }
                                }}
                                isValid={!nameError && name.trim().length > 0}
                                isProcessing={saving}
                                onSubmit={handleSave}
                                submitLabel={saving ? t('Saving...') : t('Update')}
                                onReset={() => {
                                    setEditing(false)
                                    setNameError('')
                                }}
                                resetLabel={t('Cancel')}
                                dataObjectId={dataObject?.uuid}
                                showFooter={true}
                                existingRules={existingRules}
                                rulesLoading={rulesLoading}
                                onSelectionChange={setSelectedColumnsForReport}
                                initialSelectedColumns={columns.filter(c => c.analysis_flag !== false).map(c => c.name)}
                            />
                        </div>
                    )}

                </div> {/* Close main content div */}
            </div>

            {/* Schema Comparison Modal (retrigger flow) */}
            <SchemaComparisonModal
                isOpen={showSchemaModal}
                onClose={handleSchemaRetriggerGoBack}
                onProceed={handleSchemaRetriggerProceed}
                result={schemaComparison.result}
                loading={schemaComparison.loading}
                error={schemaComparison.error}
                sourceType="table"
                dataObjectName={dataObject?.name ?? ''}
                onRetry={dataObject && isDatabricksTable(dataObject)
                    ? () => schemaComparison.compareWithTable(dataObject.uuid, getDatabricksTableName(dataObject))
                    : undefined
                }
            />
        </div>,
        document.body
    )
}

export default DataObjectActionsModal
